// PhoneActivationSection.tsx — v12.2.15.0 · VoiceSpectrum AI (T2)
//
// Shared "Phone & Activation" surface. This is the SAME phone number card + AI-Agent-enabled
// toggle + off-route control that lives in Settings → AI Agent → Phone & Activation, factored so
// the Workflow designer's "Inbound call" pre-routing card can embed it directly (one source of
// truth — not a copy). It reads and writes the exact same endpoint (/t2/settings/ai-agent[/phone-
// activation]) on the exact same react-query key (['t2-ai-agent']) that AiAgentSections uses, so an
// edit made in the workflow drawer and one made on the Settings page stay in sync both ways.
//
// The presentational PhoneCard is imported from AiAgentSections (no reimplementation). Only the
// small interactive shell (enable toggle + off-route safety net + inline save bar) is defined here.
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';
import { HelpTooltip } from './HelpTooltip';
import { PhoneCard } from '../../pages/AiAgentSections';
import type { AiAgentData } from '../../pages/AiAgentSections';
// ai-agent.css scopes .phone-card / .enable-row / .off-route under `.ai-agent-panel`, and the off-route
// segment control lives in settings-controls.css — pull both in and keep the `.ai-agent-panel` wrapper
// class on the root so the reused elements render identically to the Settings page inside the drawer.
import '../../pages/ai-agent.css';
import '../../pages/settings-controls.css';

// SAME query key value AiAgentSections uses — react-query matches keys by value, so both surfaces
// share one cache entry (edit here → Settings page reflects it, and vice versa).
const AI_AGENT_QKEY = ['t2-ai-agent'];

// The subtitle shown on the Inbound card + drawer header: "Phone · Text" when the number handles
// both voice and SMS, else "Phone". Derived from the number's real capability pills (HANDLES).
export function phoneHandlesLabel(facilities?: { name: string; ok: boolean }[] | null): string {
  if (!facilities || facilities.length === 0) return 'Phone';
  const has = (re: RegExp) => facilities.some(f => f.ok && re.test(f.name));
  const text = has(/text|sms|message/i);
  return text ? 'Phone · Text' : 'Phone';
}

// Lightweight read-only hook for the flow tree card meta: returns the handles label from cache/fetch
// without owning any draft state. Shares the cache with the section below, so it costs no extra call.
export function usePhoneHandlesLabel(): string {
  const { data } = useQuery<AiAgentData>({
    queryKey: AI_AGENT_QKEY,
    queryFn: () => api.get<AiAgentData>('/t2/settings/ai-agent'),
    staleTime: 60_000,
  });
  return phoneHandlesLabel(data?.phoneFacilities);
}

const eqx = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

// Self-contained Phone & Activation body for embedding in the Workflow "Inbound call" drawer.
/**
 * v12.9.21 (#865): the one definition of "this section has unsaved edits", used by the inline save bar AND
 * by the imperative handle the drawer's Done calls. Two copies of this would be two answers to the same
 * question, which is the whole family of defect this build is about.
 */
function phoneDirty(draft: AiAgentData | null | undefined, saved: AiAgentData | null | undefined): boolean {
  if (!draft || !saved) return false;
  return !eqx(
    [draft.agentEnabled, draft.agentOffRoute, draft.voicemailEnabled],
    [saved.agentEnabled, saved.agentOffRoute, saved.voicemailEnabled],
  );
}

/**
 * v12.9.21 (#865): what the PARENT may do to this section.
 * The Inbound-call drawer embeds this component and has its own Done button. Before this build, Done saved
 * the workflow document and never touched this section's draft, so the AI Agent toggle - the switch deciding
 * whether the agent answers at all - was silently discarded when the drawer closed.
 * UI Principles v1.4 §12: a surface must not contain two controls that both look like they commit.
 */
export interface PhoneActivationHandle {
  isDirty(): boolean;
  save(): Promise<void>;
}

export const PhoneActivationSection = forwardRef<PhoneActivationHandle, { embedded?: boolean }>(
  function PhoneActivationSection({ embedded = false }, ref) {

  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery<AiAgentData>({
    queryKey: AI_AGENT_QKEY,
    queryFn: () => api.get<AiAgentData>('/t2/settings/ai-agent'),
  });

  const [draft, setDraft] = useState<AiAgentData | null>(null);
  const [saved, setSaved] = useState<AiAgentData | null>(null);
  const [vmAuto, setVmAuto] = useState(false);
  const seeded = useRef(false);

  useEffect(() => {
    if (!data) return;
    // Re-seed from the shared cache whenever it changes (e.g. the Settings page saved), but keep the
    // user's in-flight edits if they've started editing here and nothing external changed.
    if (!seeded.current || eqx(data, saved)) {
      setSaved(structuredClone(data));
      setDraft(structuredClone(data));
      seeded.current = true;
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const savePhone = useMutation({
    mutationFn: (d: AiAgentData) => api.put<AiAgentData>('/t2/settings/ai-agent/phone-activation', {
      agentEnabled: d.agentEnabled, agentOffRoute: d.agentOffRoute, voicemailEnabled: d.voicemailEnabled,
    }),
    onSuccess: (fresh) => {
      setSaved(structuredClone(fresh));
      setDraft(structuredClone(fresh));
      setVmAuto(false);
      // Push into the shared cache so the Settings page (same key) reflects the change immediately.
      queryClient.setQueryData(AI_AGENT_QKEY, fresh);
    },
  });

  // v12.9.21 (#865): the parent's Done commits this section too.
  // THIS HOOK MUST STAY ABOVE THE EARLY RETURNS BELOW. Check-T2Hooks caught it placed underneath them, where
  // React would skip it on a loading render and then run it on the next one - React error #310, a runtime
  // crash that tsc cannot see. It therefore does its own null-guarding rather than relying on the guard.
  useImperativeHandle(ref, () => ({
    isDirty: () => phoneDirty(draft, saved),
    save: async () => { if (phoneDirty(draft, saved) && draft) { await savePhone.mutateAsync(draft); } },
  }), [draft, saved, savePhone]);

  if (isLoading || !draft || !saved) return <div className="settings-loading">Loading Phone &amp; Activation…</div>;
  if (error) return <div className="settings-error">Phone &amp; Activation could not be loaded.</div>;

  const patch = (p: Partial<AiAgentData>) => setDraft(d => (d ? { ...d, ...p } : d));

  // "Calls are never dropped" safety net — mirrors AiAgentSections: when the agent is switched off
  // and routed to voicemail, voicemail is auto-enabled; undone if the agent is switched back on.
  function toggleAgent() {
    if (!draft) return;
    const on = !draft.agentEnabled;
    const p: Partial<AiAgentData> = { agentEnabled: on };
    if (!on) {
      let route = draft.agentOffRoute;
      let vm = draft.voicemailEnabled;
      if (!draft.handoffEnabled && route === 'handoff') route = 'voicemail';
      if (route === 'voicemail' && !vm) { vm = true; setVmAuto(true); }
      if (!draft.handoffEnabled && !vm) { vm = true; route = 'voicemail'; setVmAuto(true); }
      p.agentOffRoute = route; p.voicemailEnabled = vm;
    } else if (vmAuto) {
      p.voicemailEnabled = false; setVmAuto(false);
    }
    patch(p);
  }
  function setOffRoute(v: string) {
    if (!draft) return;
    if (v === 'handoff' && !draft.handoffEnabled) return;
    const p: Partial<AiAgentData> = { agentOffRoute: v };
    if (v === 'voicemail' && !draft.voicemailEnabled) { p.voicemailEnabled = true; setVmAuto(true); }
    if (v === 'handoff' && vmAuto) { p.voicemailEnabled = false; setVmAuto(false); }
    patch(p);
  }

  const dirty = phoneDirty(draft, saved);

  return (
    <div className="ai-agent-panel pa-embed">
      <p className="pa-embed-lead">
        This is where every call begins. The number below is your live line; the toggle decides
        whether the AI Agent answers it. Anything you change here is the same setting as
        <b> Settings → AI Agent → Phone &amp; Activation</b>.
      </p>
      <PhoneCard status={draft.phoneStatus} number={draft.phoneNumber} facilities={draft.phoneFacilities} />
      <div className={`enable-row${draft.agentEnabled ? '' : ' off'}`}>
        <div className="enable-text">
          <b>AI Agent enabled <span className="aa-req">*</span></b>
          <HelpTooltip sections={[
            ['What it does', 'Master switch for the AI receptionist on inbound calls.'],
            ['When it fires', 'If off, all inbound calls route directly to the fallback handoff line or voicemail without any AI interaction.'],
            ['Example', 'Turn off during a system outage or compliance freeze — callers go straight to your front desk line.'],
          ]} />
          <span className="enable-sub">When enabled, the agent answers on this number 24/7.</span>
        </div>
        <button type="button" className={`day-toggle${draft.agentEnabled ? '' : ' off'}`} onClick={toggleAgent} aria-label="Toggle AI Agent"><span className="knob" /></button>
      </div>
      {!draft.agentEnabled && (
        <div className="off-route">
          <div className="or-top">
            <div className="or-label">When the AI Agent is off
              <HelpTooltip sections={[
                ['What it does', 'Sets where inbound calls go while the AI Agent is switched off — so there’s always a defined path and calls are never dropped.'],
                ['When it fires', 'Applies to every inbound call while the AI Agent toggle above is off.'],
                ['Example', <><em>Route to handoff</em> → calls ring your phone line / platform users. <em>Route to voicemail</em> → callers go straight to the voicemail greeting.</>],
              ]} />
              <span className="aa-hint">— so calls are never dropped</span>
            </div>
            <div className="seg-group">
              <button type="button" className={`seg${draft.agentOffRoute === 'handoff' ? ' on' : ''}`} onClick={() => setOffRoute('handoff')} disabled={!draft.handoffEnabled} title={!draft.handoffEnabled ? 'Enable Handoff to use this' : undefined}><span className="seg-dot" />Route to handoff</button>
              <button type="button" className={`seg${draft.agentOffRoute === 'voicemail' ? ' on' : ''}`} onClick={() => setOffRoute('voicemail')}><span className="seg-dot" />Route to voicemail</button>
            </div>
          </div>
          {!draft.handoffEnabled && <div className="or-note">Handoff is turned off — enable it in the Handoff section to route there.</div>}
          {vmAuto && <div className="or-note">Voicemail was turned on automatically so callers always have somewhere to go. You can customize it in the Voicemail section.</div>}
        </div>
      )}
      {/* v12.9.21 (#865): EMBEDDED means the drawer owns the commit. Rendering this bar as well is what
          made "Save" and "Done" two different promises in one drawer. §12. */}
      {dirty && !embedded && (
        <div className="pa-savebar">
          <span>Unsaved changes to Phone &amp; Activation</span>
          <button type="button" className="tenant-secondary" onClick={() => { setDraft(structuredClone(saved)); setVmAuto(false); }} disabled={savePhone.isPending}>Discard</button>
          <button type="button" className="tenant-primary" onClick={() => savePhone.mutate(draft)} disabled={savePhone.isPending}>{savePhone.isPending ? 'Saving…' : 'Save'}</button>
        </div>
      )}
    </div>
  );
});

export default PhoneActivationSection;
